<template>
  <div>
    <div class="bg-container"></div>
    <img class="labour-love" src="@/assets/images/labour-love-3.svg">

    <plants class="plants-svg"></plants>

    <div class="nurses-bear-heart">
      <div class="beat-container">
        <img class="bear-heart-core" src="@/assets/images/nurses/paws-heart.svg">
        <img class="stethoscope" src="@/assets/images/nurses/stethoscope.svg">
      </div>
      <bear-head class="bear-head"></bear-head>
      <div class="nurses-names">
        <div
          v-for="(nurse, index) in $store.getters.activeSlide.nurses"
          :key="`nurse-item-${index}`"
          class="nurse-name"
          :class="`nurse-name-${index}`">

          <name-container
            is-one-line
            use-split-text
            :text="`${nurse.firstname} ${nurse.lastname}`"
            :chars-limit-one="18"
            :chars-limit-two="21"
            :chars-limit-three="24"></name-container>

        </div>
      </div>
    </div>

    <div class="heading">
      <div class="heading-line-1">CELEBRATING OUR</div>
      <strong class="heading-line-2">NURSES</strong>
    </div>
  </div>
</template>

<script>
import { TimelineMax, Power0, Power2, Power3, Power4, Back } from 'gsap'
import SplitText from '@/assets/gsap-bonus/SplitText'
import Plants from '@/components/NursesSlide/Plants.vue'
import BearHead from '@/components/NursesSlide/BearHead.vue'
import NameContainer from '@/components/NameContainer.vue'

export default {
  name: 'DoctorsSlide',

  components: {
    Plants,
    BearHead,
    NameContainer
  },

  data () {
    return {
      tl: new TimelineMax(),
      tlMouth: new TimelineMax({ repeat: -1, repeatDelay: 3 }),
      tlName: new TimelineMax(),
      namesUpdated: false
    }
  },

  mounted () {
    // Copy elements
    const heading1 = this.$el.querySelector('.heading-line-1')
    const heading2 = this.$el.querySelector('.heading-line-2')
    const headingSplit = new SplitText(heading1, { type: 'chars' })

    // Plants
    const plant1 = this.$el.querySelector('.plant-1')
    const plant2 = this.$el.querySelector('.plant-2')
    const plant3 = this.$el.querySelector('.plant-3')
    const plant4 = this.$el.querySelector('.plant-4')
    const flowers = this.$el.querySelector('.flowers')
    const backPlants = this.$el.querySelectorAll('.plant-back')

    // Heart
    const heartMain = this.$el.querySelector('.nurses-bear-heart')
    const heart = this.$el.querySelector('.beat-container')
    const stethoscope = this.$el.querySelector('.stethoscope')
    const head = this.$el.querySelector('.bear-head')
    const leftEar = this.$el.querySelector('.left-ear')
    const rightEar = this.$el.querySelector('.right-ear')
    const mouth = this.$el.querySelector('.mouth')
    const eyelids = this.$el.querySelectorAll('.eyelid')
    const leftBrow = this.$el.querySelector('.left-brow')
    const rightBrow = this.$el.querySelector('.right-brow')

    // Mutiple timelines are necessary to achieve repeat timings
    this.tlMouth
      .delay(1.5)
      .to(mouth, 1, { scaleY: 0.8, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.5 })
      .delay(2.3)
      .to(eyelids, 0.3, { yPercent: -85, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 0.1)
      .to(leftBrow, 0.55, { xPercent: 25, yPercent: -5, rotation: 10, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 0)
      .to(rightBrow, 0.55, { xPercent: -25, yPercent: 5, rotation: -10, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 0)

    this.tl
      .delay(1.2)
      .add('heartIn', 0.3)
      .from(heartMain, 2, { yPercent: 150, ease: Power2.easeOut }, 'heartIn')
      .add('textIn', 0.6)
      .staggerFrom(headingSplit.chars, 0.8, { yPercent: 60, ease: Power3.easeOut }, 0.03, 'textIn+=0.05')
      .from(heading1, 1.7, { yPercent: 250, ease: Power4.easeOut }, 'textIn')
      .from(heading2, 1.7, { yPercent: 250, ease: Power4.easeOut }, 'textIn')
      .add('plant', 0)
      .from(plant1, 3, { scale: 0, transformOrigin: '100% 100%' }, 'plant')
      .from(plant2, 3, { scale: 0.78, xPercent: 2, transformOrigin: '60% 80%' }, 'plant')
      .from(backPlants, 3, { scale: 0.85, transformOrigin: '50% 60%' }, 'plant')
      .add('plantLoop', 'plant+=3')
      .to(plant3, 2, { rotation: -5, ease: Power0.easeNone, repeat: -1, repeatDelay: 0.15, yoyo: true }, 'plantLoop')
      .to(plant4, 2, { rotation: 5, ease: Power0.easeNone, repeat: -1, repeatDelay: 0.15, yoyo: true }, 'plantLoop')
      .to(flowers, 2, { rotation: 5, transformOrigin: '70% 100%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'plantLoop')
      .add('heartLoop', 'heartIn+=1.5')
      .to(heart, 1, { scale: 1.03, transformOrigin: '50% 30%', ease: Back.easeInOut, repeat: -1, yoyo: true }, 'heartLoop')
      .to(stethoscope, 1, { xPercent: -2, yPercent: -1, ease: Back.easeInOut, repeat: -1, yoyo: true }, 'heartLoop')
      .to(head, 1.1, { rotation: 7, scale: 1.02, transformOrigin: '55% 60%', ease: Power2.easeInOut, repeat: -1, yoyo: true }, 'heartLoop+=0.3')
      .to(leftEar, 1.1, { rotation: 8, xPercent: 10, yPercent: -5, ease: Power2.easeInOut, repeat: -1, yoyo: true }, 'heartLoop+=0.3')
      .to(rightEar, 1.1, { rotation: -10, xPercent: -12, yPercent: 4, ease: Power2.easeInOut, repeat: -1, yoyo: true }, 'heartLoop+=0.3')

    this.namesIn(2.5)
  },

  watch: {
    '$store.state.activeSlide' () {
      this.namesUpdated = true
    }
  },

  updated () {
    if (this.namesUpdated) {
      this.namesUpdated = false
      this.namesIn()
    }
  },

  beforeDestroy () {
    this.tl.kill()
    this.tlMouth.kill()
    this.tlName.kill()
  },

  methods: {
    namesIn (delay = 0) {
      const nameChars1 = this.$el.querySelectorAll('.nurse-name-0 .split-char')
      const nameChars2 = this.$el.querySelectorAll('.nurse-name-1 .split-char')
      const nameChars3 = this.$el.querySelectorAll('.nurse-name-2 .split-char')
      const nameChars4 = this.$el.querySelectorAll('.nurse-name-3 .split-char')

      this.tlName.kill()
      this.tlName = new TimelineMax()
        .delay(delay)
        .staggerFrom(nameChars1, 0.1, { opacity: 0 }, 0.03, 0)
        .staggerFrom(nameChars2, 0.1, { opacity: 0 }, 0.03, 0.25)
        .staggerFrom(nameChars3, 0.1, { opacity: 0 }, 0.03, 0.5)
        .staggerFrom(nameChars4, 0.1, { opacity: 0 }, 0.03, 0.75)
    }
  }
}
</script>

<style scoped>
.labour-love {
  position: absolute;
  height: auto;
  width: 5.7vw;
  left: 2vw;
  bottom: 2vw;
  z-index: 5;
}

.bg-container {
  background: #59bec9;
  width: 100vw;
  height: 100vh;
}

.bear-doc-svg {
  position: absolute;
  width: 41.2vw;
  left: 14.9vw;
  bottom: 0;
}

.heading {
  font-family: 'Barlow', sans-serif;
  font-weight: 300;
  font-size: 3.45vw;
  color: #fff;
  text-align: center;
  line-height: 1.1;
  overflow: hidden;
  position: absolute;
  right: 7.8vw;
  top: 26vh;
}

.heading-line-2 {
  font-weight: 500;
  font-size: 2.13em;
  overflow: hidden;
  display: block;
  letter-spacing: 0.05em;
}

.plants-svg {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 31vw;
}

.nurses-bear-heart {
  position: absolute;
  bottom: 3.5vh;
  left: 17vw;
  width: 42.25vw;
}
.beat-container {
  width: 100%;
  position: relative;
}
.bear-heart-core {
  width: 100%;
}
.stethoscope {
  position: absolute;
  width: 47.2%;
  left: -28%;
  top: -32%;
}
.bear-head {
  position: absolute;
  top: -40.5%;
  left: 15%;
  width: 58%;
  height: auto;
}

.nurses-names {
  position: absolute;
  top: 37%;
  left: 20%;
  right: 21%;
  text-align: center;
}

.nurse-name {
  font-family: 'Barlow', sans-serif;
  font-weight: 700;
  font-size: 2.1vw;
  color: #ffffff;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.nurse-name:not(:first-child) {
  margin-top: 0.45em;
}
</style>



// WEBPACK FOOTER //
// src/components/NursesSlide.vue